/**
 * /mi-reserva — Public reservation-tracking panel.
 *
 * The customer enters their email + order number to see the current status
 * of their Starlink Mini booking (visual timeline + payment / delivery info).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { motion } from "framer-motion";
import { Package, Search, CheckCircle2, Circle, Truck, Clock, AlertTriangle, ArrowLeft } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useEffect } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

const STEPS = [
  { key: "reservado", label: "Reserva confirmada", desc: "Tu plaza está guardada en el lote #1" },
  { key: "pedido_cursado", label: "Pedido cursado", desc: "Se ha cursado el pedido al fabricante" },
  { key: "saldo_pagado", label: "Saldo pagado", desc: "Preparando envío desde nuestro almacén" },
  { key: "enviado", label: "Enviado", desc: "El transportista tiene tu paquete" },
];

const STATE_INDEX = (s) => {
  const idx = STEPS.findIndex((x) => x.key === s);
  return idx === -1 ? -1 : idx;
};

export default function StarlinkUserTracking() {
  const { setLanguage } = useLanguage();
  const [form, setForm] = useState({ email: "", order_number: "" });
  const [state, setState] = useState({ loading: false, error: "", data: null });

  useEffect(() => { setLanguage("es"); document.title = "Mi reserva · Starlink Mini | GTX"; }, [setLanguage]);

  const search = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "", data: null });
    try {
      const r = await axios.post(`${API}/starlink/reservations/track`, {
        email: form.email.trim().toLowerCase(),
        order_number: form.order_number.trim().toUpperCase(),
      });
      setState({ loading: false, error: "", data: r.data });
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se ha encontrado ninguna reserva con esos datos.";
      setState({ loading: false, error: typeof msg === "string" ? msg : "Error al buscar.", data: null });
    }
  };

  const data = state.data;
  const currentIdx = data ? STATE_INDEX(data.status) : -1;
  const isCancelled = data && (data.status === "cancelado" || data.status === "reembolsado");

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white noise" data-testid="starlink-tracking-page">
      <Helmet><meta name="robots" content="noindex" /></Helmet>

      <header className="border-b border-white/8 bg-[#0A0A0F]/90 backdrop-blur-lg sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-white/70 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" />
            Inicio
          </Link>
          <Link to="/reserva-starlink-mini" className="text-sm text-blue-300 hover:text-blue-200">Volver a la campaña</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-14">
        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-blue-400 mono mb-2">Seguimiento</div>
        <h1 className="text-3xl lg:text-4xl font-black tracking-tight mb-2">Mi reserva</h1>
        <p className="text-white/60 text-sm mb-8">Introduce tu email y el nº de pedido que recibiste al reservar.</p>

        {/* Form */}
        <form onSubmit={search} className="rounded-2xl border border-white/10 bg-[#0F0F16] p-6 lg:p-7 space-y-4 mb-8" data-testid="tracking-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs text-white/60 mb-1.5">Email de la reserva</span>
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                data-testid="tk-email"
                className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500/60"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-white/60 mb-1.5">Nº de pedido (ej. GTX-SL-0001)</span>
              <input
                required value={form.order_number}
                onChange={(e) => setForm((s) => ({ ...s, order_number: e.target.value }))}
                data-testid="tk-order"
                className="w-full bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm mono uppercase focus:outline-none focus:border-blue-500/60"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={state.loading}
            data-testid="tk-search"
            className="w-full sm:w-auto bg-blue-500 hover:bg-blue-400 disabled:bg-white/10 text-white rounded-lg px-6 py-3 text-sm font-bold inline-flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {state.loading ? "Buscando…" : "Ver estado de mi reserva"}
          </button>
          {state.error && (
            <div className="text-sm text-red-300 flex items-center gap-2" data-testid="tk-error">
              <AlertTriangle className="w-4 h-4" /> {state.error}
            </div>
          )}
        </form>

        {/* Result */}
        {data && (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-blue-500/30 bg-[#0F0F16] overflow-hidden"
            data-testid="tracking-result"
          >
            <div className="p-6 border-b border-white/10 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mono">Pedido</div>
                <div className="text-2xl font-black mono tracking-wider text-blue-300">{data.order_number}</div>
                <div className="text-sm text-white/60 mt-1">{data.nombre} {data.apellidos}</div>
              </div>
              <StatusPill status={data.status} />
            </div>

            {isCancelled ? (
              <div className="p-8 text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center mb-4">
                  <AlertTriangle className="w-7 h-7 text-red-300" />
                </div>
                <div className="font-bold mb-2">Reserva {data.status === "reembolsado" ? "reembolsada" : "cancelada"}</div>
                <p className="text-sm text-white/60">Si tienes dudas escríbenos a <a href="mailto:adm@globaltechxperts.nl" className="text-blue-300 underline">adm@globaltechxperts.nl</a>.</p>
              </div>
            ) : (
              <>
                {/* Timeline */}
                <div className="p-6 lg:p-7">
                  <div className="text-xs font-bold uppercase tracking-widest text-white/50 mb-4">Progreso del pedido</div>
                  <ol className="space-y-4">
                    {STEPS.map((s, i) => {
                      const done = i <= currentIdx;
                      const active = i === currentIdx;
                      return (
                        <li key={s.key} className="flex items-start gap-4">
                          <div className={`shrink-0 w-8 h-8 rounded-full border flex items-center justify-center ${
                            done ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "bg-white/[0.03] border-white/15 text-white/40"
                          }`}>
                            {done ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-3.5 h-3.5" />}
                          </div>
                          <div className="flex-1">
                            <div className={`text-sm font-bold ${done ? "text-white" : "text-white/50"}`}>
                              {s.label} {active && <span className="ml-2 text-[10px] tracking-widest uppercase text-blue-300 mono">ahora</span>}
                            </div>
                            <div className="text-xs text-white/45 mt-0.5">{s.desc}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </div>

                {/* Amounts */}
                <div className="border-t border-white/10 p-6 lg:p-7 grid sm:grid-cols-3 gap-4">
                  <Amount label="Reserva pagada" v={fmtEUR(data.importe_reserva || 0)} good />
                  <Amount label={data.status === "saldo_pagado" || data.status === "enviado" ? "Saldo pagado" : "Saldo pendiente"} v={fmtEUR(data.importe_saldo || 0)} good={data.status === "saldo_pagado" || data.status === "enviado"} />
                  <Amount label="Total del kit" v={fmtEUR(data.precio_total || 0)} />
                </div>

                {/* Balance CTA */}
                {data.balance_checkout_url && (data.status === "reservado" || data.status === "pedido_cursado") && (
                  <div className="border-t border-amber-500/30 p-6 lg:p-7 bg-amber-500/[0.05]" data-testid="balance-cta-section">
                    <div className="flex items-start gap-4 flex-wrap">
                      <div className="shrink-0 w-11 h-11 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                        <Package className="w-5 h-5 text-amber-300" />
                      </div>
                      <div className="flex-1 min-w-[220px]">
                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-400 mono mb-1">Último paso</div>
                        <div className="text-lg font-black mb-1">Tu Starlink Mini ha llegado</div>
                        <p className="text-sm text-white/70 leading-relaxed">Paga el saldo de {fmtEUR(data.importe_saldo || 0)} y lo enviamos en 24-48 h.</p>
                      </div>
                      <a
                        href={data.balance_checkout_url}
                        data-testid="pay-balance-btn"
                        className="shrink-0 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0A0A0F] rounded-lg px-6 py-3 text-sm font-black tracking-wide transition-all"
                      >
                        Pagar saldo ({fmtEUR(data.importe_saldo || 0)})
                      </a>
                    </div>
                  </div>
                )}

                {/* Delivery */}
                <div className="border-t border-white/10 p-6 lg:p-7 grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 mono">Dirección de envío</div>
                    <div className="text-white/80">{data.direccion}</div>
                    <div className="text-white/60">{data.codigo_postal} · {data.poblacion} · {data.provincia}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 mono">Plazos</div>
                    <div className="flex items-center gap-2 text-white/80"><Clock className="w-3.5 h-3.5 text-blue-300" /> Entrega estimada: {data.plazo_entrega}</div>
                    <div className="flex items-center gap-2 text-white/60 mt-1"><Truck className="w-3.5 h-3.5" /> Máximo comprometido: {data.plazo_maximo_legal}</div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  reservado: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  pedido_cursado: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
  saldo_pagado: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  enviado: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  cancelado: "bg-red-500/15 text-red-300 border-red-500/40",
  reembolsado: "bg-red-500/15 text-red-300 border-red-500/40",
};
const STATUS_LABEL = {
  reservado: "Reservado",
  pedido_cursado: "Pedido cursado",
  saldo_pagado: "Saldo pagado",
  enviado: "Enviado",
  cancelado: "Cancelado",
  reembolsado: "Reembolsado",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-white/5 text-white/60 border-white/15";
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold uppercase tracking-widest ${cls}`}>
      <Package className="w-3.5 h-3.5" /> {STATUS_LABEL[status] || status}
    </span>
  );
}

function Amount({ label, v, good = false }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 mono">{label}</div>
      <div className={`text-lg font-black ${good ? "text-emerald-300" : "text-white"}`}>{v}</div>
    </div>
  );
}
